import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Select, MenuItem, InputLabel, FormGroup, FormControlLabel, 
  FormControl, Checkbox, TableContainer, Paper, Table, TableHead, 
  TableRow, TableCell, TableBody, IconButton, Box, Grid, Modal
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import Chip from "@mui/material/Chip";
import CancelIcon from "@mui/icons-material/Cancel";
import Card from "@mui/material/Card";
import Pagination from "@mui/material/Pagination";
import { keyframes } from "@mui/system";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";

function CreateCustomerRegistration() {
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const userid = loginData?.userid;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [openDelearModal, setOpenDelearModal] = useState(false);
  const [selectedDelearId, setSelectedDelearId] = useState(null);
  const [selectedDelearName, setSelectedDelearName] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customFieldData, setCustomFieldData] = useState({});
  const [customersList, setCustomersList] = useState([]);

  const [customerDetails, setCustomerDetails] = useState({
    customertypeid: "",
    accountBio: {
      name: "",
      contact: "",
      longitude: "",
      latitude: "",
      address: "",
      area: "",
      city: "",
      state: "",
      pin: "",
      country: "",
      pan: "",
      tan: "",
      gstin: "",
    },
  });

  useEffect(() => {
    const fetchCustomFields = async () => {
      if (!customerDetails.customertypeid) return;
      try {
        const token = loginData?.token;
        const secretKey = await fetchSecretKey();
        const response = await axios.get(
          `/api/customertype/customertypeid/${customerDetails.customertypeid}/fields`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.status === 200) {
          const decrypted = decryptBody(response.data, secretKey);
          setCustomFields(decrypted?.customFields || []);
        }
      } catch (err) {
        console.error("Error fetching custom fields:", err);
      }
    };

    fetchCustomFields();
  }, [customerDetails.customertypeid]);

  useEffect(() => {
    const fetchCustomerTypes = async () => {
      try {
        const secretKey = await fetchSecretKey();
        const token = loginData?.token;
        const response = await axios.get(`/api/customertype/disabled=false/adminid=${userid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 200) {
          const decrypted = decryptBody(response.data, secretKey);
          setCustomerTypes(decrypted);
        }
      } catch (err) {
        console.error("Failed to fetch customer types", err);
      }
    };

    fetchCustomerTypes();
  }, []);

  const handleCustomFieldChange = (id, value) => {
    setCustomFieldData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();

      const customFieldsList = customFields.map((field) => ({
        fieldid: field.id,
        fieldvalue: Array.isArray(customFieldData[field.id])
          ? customFieldData[field.id]
          : [customFieldData[field.id] || ""],
      }));

      const rawPayload = {
        adminId: userid,
        customerTypeId: customerDetails.customertypeid,
        customFieldValues: customFieldsList,
        accountBio: { ...customerDetails.accountBio },
      };

      console.log("Raw Payload Before Encryption:", rawPayload);

      const encryptedData = encryptBody(rawPayload, secretKey);
      const token = loginData?.token;

      const response = await axios.post("/api/Customerprofile/create", encryptedData, {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const decryptedResponse = decryptBody(response.data, secretKey);

        if (decryptedResponse?.status === "success") {
          toast.success(decryptedResponse.message || "Customer registered successfully!");
          navigate(-1);
          setTimeout(() => {
            toast.info("Password has been sent to your email.");
          }, 1500);

          setCustomersList((prev) => [
            ...prev,
            {
              customertypeid: customerDetails.customertypeid,
            },
          ]);

          setCustomerDetails({
            customertypeid: "",
            accountBio: {
              name: "",
              contact: "",
              longitude: "",
              latitude: "",
              address: "",
              area: "",
              city: "",
              state: "",
              pin: "",
              country: "",
              pan: "",
              tan: "",
              gstin: "",
            },
          });
        } else {
          toast.error(decryptedResponse?.message || "Failed to verify registration.");
        }
      }
    } catch (error) {
      console.error("Registration Error:", error);
      const errMsg = error.response?.data?.message || error.message || "Registration failed";
      toast.error(errMsg.includes("Account already exists")
        ? "This email is already registered."
        : errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCustomerDetails({
      customertypeid: "",
      accountBio: {
        name: "",
        contact: "",
        longitude: "",
        latitude: "",
        address: "",
        area: "",
        city: "",
        state: "",
        pin: "",
        country: "",
        pan: "",
        tan: "",
        gstin: "",
      },
    });
    navigate(-1);
  };

  const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;
  const dotPositions = [
    { top: "5%", left: "50%" },
    { top: "20%", left: "80%" },
    { top: "50%", left: "95%" },
    { top: "80%", left: "80%" },
    { top: "95%", left: "50%" },
    { top: "80%", left: "20%" },
    { top: "50%", left: "5%" },
    { top: "20%", left: "20%" },
  ];
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  zIndex: 9999,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: 80, 
                    height: 80, 
                    animation: `${spinAnimation} 1.5s linear infinite`,
                  }}
                >
                  {dotPositions.map((pos, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 8, 
                        height: 8, 
                        borderRadius: "50%",
                                           backgroundColor: "#2e7faeff",
 
                        position: "absolute",
                        ...pos,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
      

     

        
        <Card sx={{ width: "1000px", maxWidth: "1200px", padding: 3 }}>
          <MDBox pt={4} pb={3} px={3}>
            
            <form onSubmit={handleSubmit}>
            <MDTypography  sx={{  marginBottom: "20px",   textAlign: "center",fontSize: "15px", fontWeight: "bold", marginBottom: "10px" }}>Create Customer Profile</MDTypography>

            <Grid container spacing={2} justifyContent="center" sx={{ marginBottom: 8 }}>
            <Grid item xs={12} sm={6}>
            <MDInput
    select
    label="Select Customer Type"
    fullWidth
    value={customerDetails?.customertypeid || ""}
    
    onChange={(e) =>
      setCustomerDetails({
        ...customerDetails,
        customertypeid: e.target.value,
      })
    }
    required
    sx={{
        "& .MuiInputBase-root": {
          height: "45px", 
        },
        "& .MuiSelect-select": {
          paddingTop: "14px", 
        },
      }}
    
  >
    <MenuItem value="">Select Customer Type</MenuItem>
    {customerTypes.map((type) => (
      <MenuItem key={type.id} value={type.id}>
        {type.customertype}
      </MenuItem>
    ))}
  </MDInput>
</Grid>
<Grid item xs={12}>
  <Box mt={2} />
</Grid>
<Grid container spacing={2}>
<Grid item xs={12} sm={6}>
              <MDInput
                label="Name"
                fullWidth
                value={customerDetails.accountBio.name}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      name: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="Email"
                fullWidth
                value={customerDetails.accountBio.email}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      email: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="Contact"
                fullWidth
                value={customerDetails.accountBio.contact}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      contact: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="Latitude"
                fullWidth
                value={customerDetails.accountBio.latitude}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      latitude: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="Longitude"
                fullWidth
                value={customerDetails.accountBio.longitude}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      longitude: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="Address"
                fullWidth
                value={customerDetails.accountBio.address}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      address: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="Area"
                fullWidth
                value={customerDetails.accountBio.area}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      area: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="City"
                fullWidth
                value={customerDetails.accountBio.city}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      city: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="State"
                fullWidth
                value={customerDetails.accountBio.state}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      state: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="Pincode"
                fullWidth
                value={customerDetails.accountBio.pin}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      pin: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="Country"
                fullWidth
                value={customerDetails.accountBio.country}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      country: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="PAN"
                fullWidth
                value={customerDetails.accountBio.pan}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      pan: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="TAN"
                fullWidth
                value={customerDetails.accountBio.tan}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      tan: e.target.value,
                    },
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput
                label="GSTIN"
                fullWidth
                value={customerDetails.accountBio.gstin}
                onChange={(e) =>
                  setCustomerDetails((prev) => ({
                    ...prev,
                    accountBio: {
                      ...prev.accountBio,
                      gstin: e.target.value,
                    },
                  }))
                }
              />
            </Grid>

  {/* Custom Fields */}
  {customFields.map((field) => (
    <Grid  container spacing={2}  item xs={12} sm={6} key={field.id}>
      {field.fieldType === "Dropdown" ? (
 <FormControl fullWidth sx={{
  mt: 2,           
  ml: 1,           
  mb: 2,           
}}>        <InputLabel id={`select-label-${field.id}`
      
      }     sx={{ fontSize: 14, mt: 0.5 }} 
      >Select {field.fieldName}</InputLabel>
        <Select
         labelId={`select-label-${field.id}`}
         label={`Select ${field.fieldName}`}
            value={customFieldData[field.id] || ""}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            sx={{
              height: "45px",
            }}
          >
                <MenuItem value="">Select</MenuItem>

            {field.options.map((opt, index) => (
              <MenuItem key={index} value={opt}>
                {opt}
              </MenuItem>
            ))}
            
          </Select>
        </FormControl>
      ) : field.fieldType === "Checkbox" ? (
        <FormControl fullWidth sx={{
          mt: 2,           
          ml: 1,           
          mb: 2,           
        }}>
        <InputLabel
          id={`checkbox-label-${field.id}`}
          sx={{ fontSize: 14, mt: 0.5 }} // moved down a bit
        >
          {field.fieldName}
        </InputLabel>
        <Select
          labelId={`checkbox-label-${field.id}`}
          multiple
          value={Array.isArray(customFieldData[field.id]) ? customFieldData[field.id] : []}
          onChange={(e) => {
            const value = e.target.value;
            handleCustomFieldChange(field.id, Array.isArray(value) ? value : [value]);
          }}
          label={field.fieldName}
          renderValue={(selected) => {
            if (Array.isArray(selected)) {
              return selected.join(', ');
            }
            return selected || '';
          }}  
                  sx={{
            height: 40,    
            '.MuiSelect-select': {
              pt: '8px',
              pb: '8px',
              alignItems: 'center',
              minHeight: '40px',
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
                width: 250,
              },
            },
          }}
        >
          {field.options.map((opt, index) => (
            <MenuItem
              key={index}
              value={opt}
              sx={{
                // Disable background color change on selection
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                },
                // Disable background on hover when selected
                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)', // light hover effect
                },
              }}
            >
              <Checkbox
                checked={(customFieldData[field.id] || []).includes(opt)}
                sx={{
                  padding: 0,
                  marginRight: 1,
                  color: 'primary.main',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      ) : (
        <MDInput
          label={field.fieldName}
          fullWidth
          value={customFieldData[field.id] || ""}
          onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
        />
      )}
    </Grid>
  ))}
</Grid>
       
              </Grid>

              <MDBox mt={2} textAlign="center">
                <MDButton variant="gradient" color="success" type="submit">Add Customer</MDButton>&emsp;
                <MDButton variant="gradient" color="success"  onClick={handleCancel} >Cancel</MDButton>
              </MDBox>
            </form>
          </MDBox>
        </Card>
      
     
    </DashboardLayout>
  );
}

export default CreateCustomerRegistration;

